import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useDispatch } from "react-redux";
import { clearCart } from "../../redux/slices/cart";
import { useQueryClient } from "@tanstack/react-query";
import "./PaymentForm.css";

const PaymentForm = ({ orderData, onBack, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [paymentError, setPaymentError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const displayTotal = orderData?.discountedTotal
    ? parseFloat(orderData.discountedTotal).toFixed(2)
    : "0.00";

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setPaymentError("Payment system is not ready.");
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: `${orderData.billing.first_name} ${orderData.billing.last_name}`,
              email: orderData.billing.email,
              address: {
                line1: orderData.billing.address_1,
                city: orderData.billing.city,
                state: orderData.billing.state,
                postal_code: orderData.billing.postcode,
                country: orderData.billing.country || "US",
              },
            },
          },
        },
      );

      if (error) {
        setPaymentError(error.message || "Payment failed.");
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        // 🔥 1. KILL BROWSER CACHE IMMEDIATELY
        // This ensures that even if they navigate manually, they won't see old data
        queryClient.removeQueries({ queryKey: ["userLibrary"] });
        setPaymentSucceeded(true);
      }
    } catch (err) {
      setPaymentError("An unexpected error occurred.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (paymentSucceeded) {
    return (
      <section className="PaymentStep">
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <h2 style={{ color: "#4caf50", marginBottom: "20px" }}>
            🎉 Payment Successful!
          </h2>
          <p>Thank you for your order #{orderData.orderId}!</p>
          <button
            disabled={isSyncing}
            onClick={() => {
              setIsSyncing(true);

              // 1. Kill browser's "old version" of the library and product details
              queryClient.removeQueries({ queryKey: ["userLibrary"] });
              queryClient.removeQueries({ queryKey: ["product"] });

              // 2. Give the Webhook/AWS Redis 5 seconds to finish the cycle
              setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ["userLibrary"] });
                queryClient.invalidateQueries({ queryKey: ["product"] });
                dispatch(clearCart());
                window.location.href = "/";
              }, 5000);
            }}
            style={{
              marginTop: "20px",
              backgroundColor: isSyncing ? "#ccc" : "#007cba",
              color: "white",
              padding: "12px 24px",
              border: "none",
              borderRadius: "4px",
              cursor: isSyncing ? "not-allowed" : "pointer",
            }}
          >
            {isSyncing ? "Updating your Library..." : "Continue Shopping"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="PaymentStep">
      <p>PAYMENT DETAILS</p>
      <div className="billing-summary">
        <p>
          <b>Billing to:</b> {orderData.billing.first_name}{" "}
          {orderData.billing.last_name}
        </p>
        <p>
          {orderData.billing.address_1}, {orderData.billing.city}
        </p>
        <p style={{ marginTop: "10px" }}>
          <b>Total: ${displayTotal}</b>
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card-element">
          <CardElement
            options={{
              style: { base: { fontSize: "16px" } },
              hidePostalCode: true,
            }}
          />
        </div>
        {paymentError && (
          <div
            className="error-msg"
            style={{ color: "red", marginTop: "10px" }}
          >
            {paymentError}
          </div>
        )}
        <div style={{ marginTop: "20px" }}>
          <button type="button" onClick={onBack} disabled={isProcessing}>
            Back
          </button>
          <button
            type="submit"
            disabled={isProcessing || !stripe}
            style={{
              marginLeft: "10px",
              backgroundColor: "#007cba",
              color: "white",
            }}
          >
            {isProcessing ? "Processing..." : `Pay $${displayTotal}`}
          </button>
        </div>
      </form>
    </section>
  );
};

export default PaymentForm;
